
import { Component, Vue, Watch } from 'vue-property-decorator'
import NotificationCard from '@/modules/in-app-notification/components/NotificationCard.vue'
import {
  INotification,
  NotificationStatus,
  getNavigationFromLinkTo
} from '@/modules/in-app-notification/types/notificationTypes'
import { notificationEndpoints } from '..'
import eventBus from '@/eventBus'

@Component({
  name: 'TheNotificationMenu',
  components: {
    NotificationCard
  }
})
export default class TheNotificationMenu extends Vue {
  // Data properties
  isMenuOpen = false
  activeFilter: NotificationStatus = NotificationStatus.ALL
  notifications: INotification[] = []
  isLoading = false
  badgeCount = 0

  // Make NotificationStatus available in template
  NotificationStatus = NotificationStatus

  // Computed properties
  get displayedNotifications(): INotification[] {
    return this.notifications.slice(0, 4)
  }

  get isRtl(): boolean {
    return this.$vuetify.rtl
  }

  get isOnNotificationsPage(): boolean {
    return this.$route.name === 'in-app-notifications'
  }

  // Methods
  navigateToNotifications(): void {
    this.$router.push({ name: 'in-app-notifications' })
    this.isMenuOpen = false
  }

  setFilter(filter: NotificationStatus): void {
    this.activeFilter = filter
  }

  async markAllAsRead(): Promise<void> {
    await notificationEndpoints.markAllAsRead().then(async () => {
      await this.loadNotifications()
      await this.getBadgeCount()
      // Emit refresh event to update the page
      eventBus.$emit('refreshMenuData')
    })
  }

  async handleNotificationClick(notification: INotification): Promise<void> {
    // Mark as read if unread
    if (notification.status === NotificationStatus.UNREAD) {
      await notificationEndpoints.markAsRead(notification.id).then(async () => {
        await this.loadNotifications()
        await this.getBadgeCount()
        // Emit refresh event to update the page
        eventBus.$emit('refreshMenuData')
      })
    }

    const updatedNotification = {
      ...notification,
      status: NotificationStatus.READ // Always set as READ
    }

    this.$router.push({
      path: `/in-app-notifications/${notification.id}`,
      query: {
        data: encodeURIComponent(JSON.stringify(updatedNotification))
      }
    })

    // Close menu
    this.isMenuOpen = false
  }

  async handleExternalLinkClicked(notification: INotification) {
    // Mark as read if unread
    if (notification.status === NotificationStatus.UNREAD) {
      await notificationEndpoints.markAsRead(notification.id).then(async () => {
        await this.loadNotifications()
        await this.getBadgeCount()
        // Emit refresh event to update the page
        eventBus.$emit('refreshMenuData')
      })
    }
    // Handle navigation if linkTo exists
    if (notification.linkTo) {
      const navigationRoute = getNavigationFromLinkTo(notification.linkTo)
      if (navigationRoute) {
        this.$router.push(navigationRoute)
      }
    }

    // Close menu
    this.isMenuOpen = false
  }

  async getBadgeCount(): Promise<void> {
    await notificationEndpoints
      .getBadgeCount()
      .then(count => (this.badgeCount = count))
  }

  async loadNotifications(): Promise<void> {
    this.isLoading = true
    const filters: Record<string, NotificationStatus> = {}

    // Only add status filter if it's not ALL
    if (this.activeFilter !== NotificationStatus.ALL) {
      filters.status = this.activeFilter
    }

    await notificationEndpoints
      .dataTable({
        filters
      })
      .then(response => {
        this.notifications = response.results
      })
      .finally(() => {
        this.isLoading = false
      })
  }

  async handleRefreshData(): Promise<void> {
    await Promise.all([this.loadNotifications(), this.getBadgeCount()])
  }

  // Watchers
  @Watch('activeFilter')
  onActiveFilterChange(): void {
    Promise.all([this.loadNotifications(), this.getBadgeCount()])
  }

  mounted() {
    if (!this.isOnNotificationsPage)
      Promise.all([this.loadNotifications(), this.getBadgeCount()])

    // Register for refresh events from the page component
    eventBus.register('refreshPageData', this.handleRefreshData)
  }

  beforeDestroy() {
    // Clean up event listeners
    eventBus.deregister('refreshPageData', this.handleRefreshData)
  }
}
